import React, { useState } from "react";
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  User,
  CheckCircle,
  Stethoscope,
  Heart,
  Calendar,
  MessageSquare,
} from "lucide-react";
import { doctorData as originalDoctorData } from "../mockData";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";
import doctorImage from "../styles/portrait-indian-doctor-hospital-corridor_734610-17779.avif";

const doctorData = {
  ...originalDoctorData,
  phone: "+91 98765 43210",
};

const DoctorPortfolio = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    date: "",
    message: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    console.log("Appointment request:", formData);
    toast({
      title: "Appointment Request Sent!",
      description: "We'll contact you shortly to confirm your appointment.",
    });
    setFormData({ name: "", phone: "", date: "", message: "" });
  };

  const scrollToAppointment = () => {
    document
      .getElementById("appointment-section")
      ?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="doctor-portfolio">
      {/* Header */}
      <header className="portfolio-header">
        <div className="header-content">
          <div className="doctor-info">
            <h1 className="doctor-name">{doctorData.name}</h1>
            <p className="doctor-specialization">{doctorData.specialization}</p>
          </div>
          <div className="header-actions">
            <a href={`tel:${doctorData.phone}`} className="phone-link">
              <Phone className="icon" size={20} />
              <span>{doctorData.phone}</span>
            </a>
            <Button
              onClick={scrollToAppointment}
              className="btn-primary btn-appointment"
            >
              Book Appointment
            </Button>
          </div>
        </div>
      </header>

      {/* Introduction */}
      <section className="section intro-section">
        <div className="container">
          <div className="intro-grid">
            <div className="intro-image-container">
              <img
                src={doctorImage}
                alt={doctorData.name}
                className="intro-image"
              />
            </div>
            <div className="intro-content">
              <h2 className="section-title">Welcome to My Practice</h2>
              <p className="intro-text">{doctorData.introduction}</p>
              <div className="quick-stats">
                <div className="stat-item">
                  <Stethoscope className="stat-icon" size={32} />
                  <div>
                    <div className="stat-value">
                      {doctorData.experience}+ Years
                    </div>
                    <div className="stat-label">Experience</div>
                  </div>
                </div>
                <div className="stat-item">
                  <Heart className="stat-icon" size={32} />
                  <div>
                    <div className="stat-value">5000+</div>
                    <div className="stat-label">Patients Treated</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills & Expertise */}
      <section className="section expertise-section">
        <div className="container">
          <h2 className="section-title">Areas of Expertise</h2>
          <div className="expertise-grid">
            {doctorData.expertise.map((skill, index) => (
              <div key={index} className="expertise-item">
                <CheckCircle className="check-icon" size={20} />
                <span>{skill}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Education & Experience */}
      <section className="section education-section">
        <div className="container">
          <h2 className="section-title">Education & Professional Experience</h2>
          <div className="education-experience-grid">
            <div className="education-block">
              <h3 className="subsection-title">Education & Certifications</h3>
              <div className="list-items">
                {doctorData.education.map((edu, index) => (
                  <Card key={index} className="info-card">
                    <CardContent className="card-content">
                      <h4 className="card-title-small">{edu.degree}</h4>
                      <p className="card-text">{edu.institution}</p>
                      <p className="card-year">{edu.year}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
            <div className="experience-block">
              <h3 className="subsection-title">Professional Experience</h3>
              <div className="list-items">
                {doctorData.experience_history.map((exp, index) => (
                  <Card key={index} className="info-card">
                    <CardContent className="card-content">
                      <h4 className="card-title-small">{exp.role}</h4>
                      <p className="card-text">{exp.organization}</p>
                      <p className="card-year">{exp.duration}</p>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section services-section">
        <div className="container">
          <h2 className="section-title">Services Provided</h2>
          <div className="services-grid">
            {doctorData.services.map((service, index) => (
              <Card key={index} className="service-card">
                <CardHeader>
                  <CardTitle className="service-title">
                    {service.title}
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="service-description">
                    {service.description}
                  </CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Clinic Information */}
      <section className="section clinic-section">
        <div className="container">
          <h2 className="section-title">Clinic Information</h2>
          <Card className="clinic-card">
            <CardContent className="clinic-content">
              <div className="clinic-info-grid">
                <div className="clinic-info-item">
                  <MapPin className="clinic-icon" size={24} />
                  <div>
                    <h4 className="info-label">Address</h4>
                    <p className="info-text">{doctorData.clinic.address}</p>
                  </div>
                </div>
                <div className="clinic-info-item">
                  <Clock className="clinic-icon" size={24} />
                  <div>
                    <h4 className="info-label">Clinic Hours</h4>
                    {doctorData.clinic.timings.map((timing, index) => (
                      <p key={index} className="info-text">
                        <strong>{timing.days}:</strong> {timing.hours}
                      </p>
                    ))}
                  </div>
                </div>
                <div className="clinic-info-item">
                  <Phone className="clinic-icon" size={24} />
                  <div>
                    <h4 className="info-label">Contact</h4>
                    <p className="info-text">{doctorData.phone}</p>
                    <p className="info-text">{doctorData.email}</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Patient Testimonials */}
      <section className="section testimonials-section">
        <div className="container">
          <h2 className="section-title">What Patients Say</h2>
          <div className="testimonials-grid">
            {doctorData.testimonials.map((testimonial, index) => (
              <Card key={index} className="testimonial-card">
                <CardContent className="testimonial-content">
                  <div className="quote-mark">"</div>
                  <p className="testimonial-text">{testimonial.text}</p>
                  <div className="testimonial-author">
                    <User className="author-icon" size={20} />
                    <div>
                      <p className="author-name">{testimonial.name}</p>
                      <p className="author-age">Age {testimonial.age}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Appointment Form */}
      <section id="appointment-section" className="section appointment-section">
        <div className="container">
          <h2 className="section-title">Book an Appointment</h2>
          <Card className="appointment-card">
            <CardContent className="appointment-content">
              <form onSubmit={handleSubmit} className="appointment-form">
                <div className="form-group">
                  <label htmlFor="name" className="form-label">
                    Full Name *
                  </label>
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    required
                    value={formData.name}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter your full name"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="phone" className="form-label">
                    Phone Number *
                  </label>
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    required
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="+91 98765 43210"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="date" className="form-label">
                    Preferred Date *
                  </label>
                  <Input
                    id="date"
                    name="date"
                    type="date"
                    required
                    value={formData.date}
                    onChange={handleInputChange}
                    className="form-input"
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="message" className="form-label">
                    Message / Reason for Visit
                  </label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    className="form-textarea"
                    placeholder="Please describe your health concern or reason for visit"
                    rows={4}
                  />
                </div>
                <Button type="submit" className="btn-submit">
                  <Calendar className="btn-icon" size={20} />
                  Submit Appointment Request
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Contact Section */}
      <section className="section contact-section">
        <div className="container">
          <h2 className="section-title">Get in Touch</h2>
          <div className="contact-grid">
            <Card className="contact-card">
              <CardContent className="contact-item">
                <Phone className="contact-icon" size={32} />
                <h4 className="contact-label">Phone</h4>
                <a href={`tel:${doctorData.phone}`} className="contact-link">
                  {doctorData.phone}
                </a>
              </CardContent>
            </Card>
            <Card className="contact-card">
              <CardContent className="contact-item">
                <Mail className="contact-icon" size={32} />
                <h4 className="contact-label">Email</h4>
                <a href={`mailto:${doctorData.email}`} className="contact-link">
                  {doctorData.email}
                </a>
              </CardContent>
            </Card>
            <Card className="contact-card">
              <CardContent className="contact-item">
                <MapPin className="contact-icon" size={32} />
                <h4 className="contact-label">Clinic Address</h4>
                <p className="contact-text">{doctorData.clinic.address}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="portfolio-footer">
        <div className="container">
          <div className="footer-content">
            <div className="disclaimer">
              <h4 className="disclaimer-title">Medical Disclaimer</h4>
              <p className="disclaimer-text">
                The information provided on this website is for educational
                purposes only and should not be considered as medical advice.
                Always consult with a qualified healthcare provider for proper
                diagnosis and treatment. In case of medical emergency, please
                call emergency services immediately.
              </p>
            </div>
            <div className="copyright">
              <p>
                © {new Date().getFullYear()} {doctorData.name}. All rights
                reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default DoctorPortfolio;
